import { NgClass, NgIf } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewContainerRef } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import {Router} from '@angular/router';
import {forkJoin, Observable, Subject} from 'rxjs';
import {DuplicateService} from '../../duplikit/duplicate.service';
import { LoadingComponent } from '../../loading/loading.component';
import {DataToolsApps, DataToolsAppsTitles} from '../../shared/DataToolsApps';
import {DummyDataService} from '../../shared/services/dummy-data.service';
import { MessageNotificationService } from '../../shared/services/message-notification.service';
import { WeightFieldComponent } from './weight-field/weight-field.component';
import { WeightValueSelectorComponent } from './weight-value-selector/weight-value-selector.component';
import {WeightService} from './weight.service';
import {IBullhornFields, ICustomPairings, IEntityWeights} from './weights-interfaces';

@Component({
  selector: 'app-weight-table',
  templateUrl: './weight-table.component.html',
  imports: [
    NgClass,
    MatTooltip,
    NgIf,
    LoadingComponent,
    WeightValueSelectorComponent,
    WeightFieldComponent
  ],
  styleUrls: ['./weight-table.component.scss']
})
export class WeightTableComponent implements OnInit, OnDestroy {

  @Output()
  needsSavingEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  needSaving = false;
  saving = false;
  loading = false;
  loadTriggered = false;
  unsavedChangesWarning = 'Leaving this page will discard any unsaved changes.';
  changesWarningHasFired = false;
  toastTitle = `${DataToolsAppsTitles.DUPLIKIT} Weights`;
  isNotComplete = {
    Candidate: false,
    ClientContact: false,
    ClientCorporation: false
  };

  dataToolsApps = DataToolsApps;
  dataToolsAppsTitles = DataToolsAppsTitles;

  constructor(
    public router: Router,
    public weightService: WeightService,
    private dummyDataService: DummyDataService,
    private view: ViewContainerRef,
    private duplicateService: DuplicateService,
    private messageNotificationService: MessageNotificationService
  ) {
  }

  ngOnInit() {}

  expandAndLoad(): void {
    this.loadTriggered = true;
    this.loading = true;

    this.duplicateService.pollEntityDedupeState('candidate').subscribe((res) => {
      this.isNotComplete['Candidate'] = res['seedState'] === 'RUNNING';
    });
    this.duplicateService.pollEntityDedupeState('contact').subscribe((res) => {
      this.isNotComplete['ClientContact'] = res['seedState'] === 'RUNNING';
    });
    this.duplicateService.pollEntityDedupeState('company').subscribe((res) => {
      this.isNotComplete['ClientCorporation'] = res['seedState'] === 'RUNNING';
    });

    this.weightService.weightsChanged = new Subject;
    this.weightService.weightsChanged.subscribe((_) => {
      if (!this.changesWarningHasFired) {
        this.messageNotificationService.warning('Leaving this page will discard any unsaved changes.');
        this.changesWarningHasFired = true;
      }
      this.needSaving = true;
      this.needsSavingEmitter.emit(this.needSaving);
    });

    const getAllCustomSelections$: Observable<ICustomPairings> = this.weightService.getAllCustomSelections();
    const getAvailableWeightsForSelection$: Observable<IBullhornFields> = this.weightService.getAvailableWeightsForSelection();
    const getAllWeights$: Observable<IEntityWeights> = this.weightService.getAll();

    const result$ = forkJoin({
      allWeights: getAllWeights$,
      bullhornFields: getAvailableWeightsForSelection$,
      customSelections: getAllCustomSelections$
    });

    result$.subscribe(({allWeights, bullhornFields, customSelections}) => {
      this.weightService.customWeightSelection = customSelections;
      this.weightService.BHFieldOptions = bullhornFields;
      this.weightService.availableBHOptions = bullhornFields;
      this.weightService.weights = allWeights;

      this.loading = false;
    }, (errorReceived) => {
      this.messageNotificationService.error('Failed to load weight data. Please reload and try again.', errorReceived);
    });

  }

  anEntitySyncIsNotComplete(): boolean {
    return this.isNotComplete['Candidate'] || this.isNotComplete['ClientContact'] || this.isNotComplete['ClientCorporation'];
  }

  private cleanState(): void {
    for (const entity of ['candidate', 'contact', 'company']) {
      this.weightService.hasChanged[entity] = false;
      this.weightService.needsResync[entity] = false;
    }
  }

  ngOnDestroy() {
    if (this.weightService.weightsChanged)
      this.weightService.weightsChanged.complete();
  }
}
